// @ts-ignore
import config from "../services/Api-config";
// @ts-ignore
import storage from "@/storage/storage";
import Vue from "vue";
import qs from "query-string";
import {
  IActionButtonPermission,
  IConfigurationPermission,
  IConfigurationPermissionItem,
  IMenuChildPermission,
  IMenuNavigation,
  IMenuPermission,
  IRoutePermission,
  IWidgetPermission
} from "@/permissions/IMenuNavigation";
import { Route } from "vue-router";
import { AccessType, ConfigTypeEnum, PermissionEnum } from "@/permissions/PermissionEnum";
import { ActionButtonEnum, WidgetEnum } from "@/permissions/ActionButtonEnum";
import { FilterOperator } from "@/models/FilterOperator";
import Auth from "@/services/Auth";
import ILocalStorageUserInformation from "@/interface/ILocalStorageUserInformation";

const date = new Date();

const PERMISSION_VERSION = "permission-version";
const CACHE_VERSION_CURRENT = date.toISOString().slice(0,10);

const APP_NAME = "FINANCE";
const APP_CONFIG = "CONFIG";
const PERMISSION_USER_KEY = "permission";
const PERMISSION_KEY = "permission-keys";
const PERMISSION_URL = "/my/permissions";
const SystemAdminModule = [
  "/sys-admin-module/"
];

let cachedPermission: IPermissionCache | null = null;
let cachedPermissionLoaded = false;
const cachedPermissionByHotel = new Map<string, PermissionItem[]>();

function invalidatePermissionCache(): void {
  cachedPermission = null;
  cachedPermissionLoaded = false;
  cachedPermissionByHotel.clear();
}

// Re-reading localStorage to validate would copy the ~1 MB blob on every call, so invalidation is event-driven.
if (typeof window != "undefined"){
  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key == PERMISSION_KEY || event.key == null){
      invalidatePermissionCache();
    }
  });
}

const PermissionHelper = {
  salt: "3jfd9283fh8",
  /**
   * Simple encrypt with salt a text to prevent exposing the real value in local storage.
   * Eg:
   * const salt = "123"
   * const permissionEncrypt = PermissionHelper.encrypt();
   *
   * console.log(permissionEncrypt("test")); //will print encrypted value
   * @param salt
   * @returns function
   */
  encrypt(salt: string) {
    const textToChars = text => text.split("").map(c => c.charCodeAt(0));
    const byteHex = n => ("0" + Number(n).toString(16)).substr(-2);
    const applySaltToChar = code => textToChars(salt).reduce((a,b) => a ^ b, code);

    return text => text.split("")
      .map(textToChars)
      .map(applySaltToChar)
      .map(byteHex)
      .join("");
  },
  /**
   * Simple decrypt with salt a text to get value from local storage.
   * Eg:
   * const salt = "123"
   * const permissionDecrypt = PermissionHelper.decrypt();
   *
   * console.log(permissionDecrypt("f2983hf92h398f")); //will print decrypted value
   * @param salt
   * @returns {Request}
   */
  decrypt(salt: string) {
    const textToChars = text => text.split("").map(c => c.charCodeAt(0));
    const applySaltToChar = code => textToChars(salt).reduce((a,b) => a ^ b, code);

    return encoded => encoded.match(/.{1,2}/g)
      .map(hex => parseInt(hex, 16))
      .map(applySaltToChar)
      .map(charCode => String.fromCharCode(charCode))
      .join("");
  },
  /**
   * Store user information (ID and is_system_admin value) to local storage
   * @param myProfileResponse
   */
  storeAndEncryptUserInformation(myProfileResponse: any): void {
    const toEncrypt = JSON.stringify(<ILocalStorageUserInformation>{
      id: myProfileResponse.Data.id,
      admin: myProfileResponse.Data.is_system_admin,
      internal_user: myProfileResponse.Data.is_internal_user,
      force_change_password: myProfileResponse.Data.force_change_password,
      force_google_login: myProfileResponse.Data.force_google_login,
    });
    const permissionEncrypt = this.encrypt(this.salt);
    localStorage.setItem(PERMISSION_USER_KEY, permissionEncrypt(toEncrypt));
  },
  /**
   * Remove cached user information
   */
  removeEncryptedUserInformation(): void {
    storage.removeItem("user");
    localStorage.removeItem(PERMISSION_USER_KEY);
  },
  /**
   * Check if current user is super admin
   */
  getUserInformation(): ILocalStorageUserInformation | null {
    try {
      const permissionDecrypt = this.decrypt(this.salt);
      const userInfoRaw: string | null = localStorage.getItem(PERMISSION_USER_KEY);
      return <ILocalStorageUserInformation> JSON.parse(permissionDecrypt(userInfoRaw));
    } catch (e) {
      return null;
    }
  },
  /**
   * Check if current user is super admin
   */
  isSuperAdmin(): boolean {
    try {
      const userInfo: ILocalStorageUserInformation|null = this.getUserInformation();
      return userInfo != null && typeof userInfo.admin != "undefined" ? userInfo.admin : false;
    } catch (e) {
      return false;
    }
  },
  /**
   * Check if current user is internal member
   */
  isInternalUser(): boolean {
    const userInfo = this.getUserInformation();
    return userInfo?.internal_user ?? false;
  },
  /**
   * Check if current user need to change password
   */
  isRequireChangePassword(): boolean {
    try {
      const userInfo: ILocalStorageUserInformation|null = this.getUserInformation();
      return userInfo != null && typeof userInfo.force_change_password != "undefined" ? userInfo.force_change_password : false;
    } catch (e) {
      return false;
    }
  },
  /**
   * Generate request to populate permission list.
   * @returns {Request}
   */
  async _generateRequestPermission (): Promise<Request> {
    const loginToken = <string> await storage.getItem(config.STORAGE_KEYS.TOKEN);
    const url = await this.constructPermissionEndpointBasedOnUser();
    return new Request((config.SENTEC_ACCOUNTS_PROFILE + url), {
      headers: {
        Accept: "application/json",
        token: loginToken
      },
      method: "GET"
    });
  },
  /**
   * Get endpoint for user permissions
   * @returns {string}
   */
  async constructPermissionEndpointBasedOnUser(): Promise<string> {
    const userToken =await Auth.GetTokenPayload();
    const url = `${ PERMISSION_URL }2/${ userToken?.uid }/v1?origin=${ location.origin }`;
    // const url = `${ PERMISSION_URL }?origin=${ location.origin }`;
    return url;
  },
  /**
   * Get current user permission from cache API.
   * @returns {string}
   */
  async getApiPermission(): Promise<Response | undefined> {
    const permissionRequest = await this._generateRequestPermission();
    const cacheApi = await caches.open(PERMISSION_KEY);
    const response = await cacheApi.match(permissionRequest);
    if (typeof response != "undefined"){
      try {
        const data = await response.json();
        return data.Data;
      } catch (e) {
        Auth.SignOut().then(() => {
          window.location.reload();
        });
      }
    }
    return response;
  },
  /**
   * Get current user permission from local storage.
   * @returns {string}
   */
  getPermission(): IPermissionCache | null {
    if (cachedPermissionLoaded){
      return cachedPermission;
    }
    const permission = localStorage.getItem(PERMISSION_KEY);
    cachedPermission = permission != null ? <IPermissionCache> JSON.parse(permission) : null;
    cachedPermissionLoaded = true;
    return cachedPermission;
  },
  /**
   * Store permission string from API to local storage.
   * @param {string} permissions String permission from permission API result
   * @returns {void}
   */
  setPermission(permissions: string): void {
    localStorage.setItem(PERMISSION_KEY, permissions);
    invalidatePermissionCache();
  },
  /**
   * Remove local storage permission. This will be called when the user logout.
   * @returns {void}
   */
  removePermission(): void {
    invalidatePermissionCache();
    localStorage.removeItem(PERMISSION_VERSION);
    localStorage.removeItem(PERMISSION_KEY);
    storage.removeItem("user").then(r => {});
    storage.removeItem("my-hotels").then(r => {});
    storage.removeItem("my-outlets").then(r => {});
    this.removeEncryptedUserInformation();
    this._generateRequestPermission().then((permissionRequest) => {
      caches.open(PERMISSION_KEY).then((cacheApi) => {
        cacheApi.delete(permissionRequest).then(() => {});
      });
    });
  },
  /**
   * Validate cache version from local-storage
   * @returns {boolean}
   */
  checkValidCacheVersion(): boolean {
    const cacheVersion = <IPermissionCache | null> this.getPermission();
    return cacheVersion?.version == CACHE_VERSION_CURRENT;
  },
  /**
   * Store permission cache api
   * @returns {void}
   */
  async getPermissionCacheApi(): Promise<Response | undefined> {
    const cachePermission = await this.getApiPermission();
    if (typeof cachePermission == "undefined" || !this.checkValidCacheVersion()){
      const url = await this.constructPermissionEndpointBasedOnUser();
      const token = await storage.getItem<string>("arch-pms-token");
      const response = await fetch(`${config.SENTEC_ACCOUNTS_PROFILE}sm${url}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "token": token == null ? "" : token.toString()
        }
      });
      const resultCache = await response.clone();
      const result = await response.json();

      const permissionRequest = await this._generateRequestPermission();
      const cacheApi = await caches.open(PERMISSION_KEY);
      await cacheApi.put(permissionRequest, resultCache);
      return new Response(JSON.stringify(result.Data));
    } else {
      return cachePermission;
    }
  },
  /**
   * Load permission from API based on current user login.
   * @returns {void}
   */
  loadPermission (callback?: any): void {
    const self = this;
    Auth.IsLogin().then((result) => {
      if (result && !self.checkValidCacheVersion()){
        this.getPermissionCacheApi().then((result) => {
          self.setPermission(JSON.stringify({
            version: CACHE_VERSION_CURRENT,
            items: result
          }));
          if (callback) {
            callback();
          }
        });
      }
    });
  },
  /**
   * Load permission from API based on current user login. (Using async)
   * @returns {void}
   */
  async loadPermissionAsync (): Promise<void> {
    const loggedIn = await Auth.IsLogin();
    if (loggedIn && !this.checkValidCacheVersion()){
      const response = await this.getPermissionCacheApi();
      const result = await response?.json();
      this.setPermission(JSON.stringify({
        version: CACHE_VERSION_CURRENT,
        items: result
      }));
    }
  },
  /**
   * Show ArchNotify message when user doesn't have permission
   * @param message
   * @returns {boolean}
   */
  showAlertPermission(message = "You don't have permission to perform this action."): true {
    Vue.prototype.$archNotify({
      showClose: true,
      message: message,
      color: "warning",
      textColor: "black--text"
    });
    return true;
  },
  /**
   * Check user access if they able to see an action button
   * @param {ActionButtonEnum} buttonName Name of the button
   * @returns {boolean}
   */
  canAccessButton(buttonName: ActionButtonEnum): boolean {
    return this.canAccess({ type: PermissionEnum.ACTION_BUTTON, item: buttonName });
  },
  /**
   * Check user access if they able to see widget (card or chart)
   * @param {WidgetEnum} widgetName Name of the widget(code)
   * @returns {boolean}
   */
  canAccessWidget(widgetName: WidgetEnum): boolean {
    return this.canAccess({ type: PermissionEnum.WIDGET, item: widgetName });
  },
  /**
   * Check if current user able to access a menu or route.
   * Return as boolean, later developer can decide what to do if the user don't have access to menu/route
   * @param {IMenuPermission | IMenuChildPermission | IRoutePermission | IActionButtonPermission | IConfigurationPermission | IWidgetPermission} params Object of the access, it can be menu/route/widget/action button.
   * @returns {boolean}
   */
  canAccess(params: IMenuPermission | IMenuChildPermission | IRoutePermission | IActionButtonPermission | IConfigurationPermission | IWidgetPermission): boolean {
    // Bypass any permission check if the current user super admin
    if (this.isSuperAdmin()){
      return true;
    }
    let list: IPermissionCache | null = this.getPermission();
    // Load permission first if is empty.
    if (list == null){
      this.loadPermission();
      list = this.getPermission();
    }
    const query = qs.parse(location.search) as any;
    const currentHotelId = query.hotelId;
    /*
      Allow the user to access the page, when they haven't choose the hotel, but it will hide all the menus.
      This only used for app that using hotel ID.
       */
    // @ts-ignore
    if (APP_NAME != "ACCOUNT" && typeof currentHotelId == "undefined" && params.type == "route"){
      return true;
    }
    // After the system try to get the permission list and it's still null then return it as false.
    if (list == null){
      return false;
    }
    let listUserPermission: PermissionItem[] = this._getPermissionByHotelId(currentHotelId);
    if (params.type == PermissionEnum.MENU && params.item.app){
      listUserPermission = this._getPermissionByHotelId(currentHotelId, params.item.app);
    }
    switch (params.type){
      case PermissionEnum.MENU:
        return this._validateMenuPermission(listUserPermission, params.item);
      case PermissionEnum.MENU_CHILD:
        return this._validateChildMenuPermission(listUserPermission, params.item);
      case PermissionEnum.ROUTE:
        return this._validateRoutePermission(listUserPermission, params.item);
      case PermissionEnum.ACTION_BUTTON:
        return this._validateActionButtonPermission(listUserPermission, params);
      case PermissionEnum.CONFIGURATION:
        return this._validatePermissionConfiguration(listUserPermission, params.item);
      case PermissionEnum.WIDGET:
        return this._validatePermissionWidget(listUserPermission, params.item);
      default:
        return false;
    }
  },
  /**
   * Get all permission configuration based on user & role
   * Return as PermissionItem[] when asFilterObject is false, and string when asFilterObject is true
   * @param {string} hotelId Current hotel ID.
   * @param {ConfigTypeEnum} configurationType Configuration type for filter permission.
   * @returns {string}
   */
  getQueryStringPermissionConfigurationByType(hotelId: string | undefined, configurationType: ConfigTypeEnum): string {
    const permissions = this._getPermissionByHotelId(hotelId);
    const filteredPermissions = permissions.filter(r => r.app == APP_CONFIG && r.code == configurationType);
    if (this.isSuperAdmin()){
      return "";
    }
    if (permissions.filter(r => r.app == APP_CONFIG && r.code == configurationType && r.resource == "*").length > 0){
      return "";
    }
    let filter_by: string[] = ["id"];
    let filter_by_value: any = ["0"];
    let filter_by_operator: FilterOperator[] = [FilterOperator.Equal];
    if (filteredPermissions.length == 0){
      return "?" + qs.stringify(
        { filter_by, filter_by_value, filter_by_operator },
        { arrayFormat: "bracket" }
      );
    }
    const samplePermission = filteredPermissions[0];
    let listKey: string[] = [];
    for (let index = 0; index <= filteredPermissions.length; index++){
      const singleData = filteredPermissions[index];
      if (singleData) {
        let singleValue: string[] = [];
        if (singleData.resource) {
          const configurationValue = singleData.resource.toString();
          try {
            singleValue = JSON.parse(configurationValue);
          } catch (e) {
            singleValue = [configurationValue];
          }
        }
        listKey = listKey.concat(singleValue);
      }
    }

    filter_by = [samplePermission.source_column];
    filter_by_value = [listKey];
    filter_by_operator = [FilterOperator.InList];
    return "?" + qs.stringify(
      { filter_by, filter_by_value, filter_by_operator },
      { arrayFormat: "bracket" }
    );
  },
  /**
   * Get all urls in a menu including the child, and combine it as string[].
   * @param {IMenuNavigation[]} childMenu Menu that have a child inside.
   * @returns {string[]}
   */
  _getAllUrls(childMenu: IMenuNavigation[]): string[] {
    const listUrl: string[] = [];
    for (const index in childMenu){
      const data = childMenu[index];
      if (typeof data.link != "undefined"){
        listUrl.push(data.link);
      }
      if (typeof data.items != "undefined" && data.items.length > 0){
        const childLink = <string[]><unknown> this._getAllUrls(<IMenuNavigation[]>data.items);
        for (const indexChild in childLink){
          listUrl.push(childLink[indexChild]);
        }
      }
    }
    return listUrl;
  },
  /**
   * Get all permission that user owned for specific hotel.
   * @param {string} hotelId Current hotel ID.
   * @param {string} appName App name for collecting available permission
   * @returns {PermissionItem[]}
   */
  _getPermissionByHotelId(hotelId: string | undefined, appName: string = APP_NAME): PermissionItem[] {
    const cacheKey = `${typeof hotelId == "undefined" ? "" : hotelId}|${appName}`;
    const cached = cachedPermissionByHotel.get(cacheKey);
    if (typeof cached != "undefined"){
      return cached;
    }
    const list: IPermissionCache | null = PermissionHelper.getPermission();
    const result: PermissionItem[] = [];
    if (list == null){
      return result;
    }
    const parsedList = list;
    for (const index in parsedList.items){
      const singlePermission = new PermissionItem(parsedList.items[index]);
      if (typeof hotelId != "undefined" && singlePermission.hotelId.indexOf(hotelId) > -1 && (singlePermission.app == appName || singlePermission.app == APP_CONFIG)){
        result.push(singlePermission);
      }
      if (typeof hotelId == "undefined" && (singlePermission.app == appName  || singlePermission.app == APP_CONFIG)){
        result.push(singlePermission);
      }
    }
    cachedPermissionByHotel.set(cacheKey, result);
    return result;
  },
  /**
   * Get all menus inside of the children (if any)
   * @returns {string[]}
   */
  _getAllMenus(menu: IMenuNavigation): string[] {
    const allLinkInMenu: string[] = [];
    // Get all possible link in a menu including child.
    // Link is the single menu
    if ("link" in menu) {
      allLinkInMenu.push(menu.link);
    }
    // Items is for the parent menu that having child inside of it.
    if ("items" in menu && typeof menu.items != "undefined" && menu.items.length > 0){
      const menuChild = <string[]> this._getAllUrls(<IMenuNavigation[]>menu.items);
      for (const indexChild in menuChild){
        allLinkInMenu.push(menuChild[indexChild]);
      }
    }
    return allLinkInMenu;
  },
  /**
   * Validate if the user has all access permission
   * @param {PermissionItem} itemPermission Object of permission item.
   * @param {boolean} isWrite? Is for write access or not.
   * @returns {boolean}
   */
  _validateAllAccessPermission(itemPermission: PermissionItem, isWrite?): boolean {
    // Check for all access permission (read)
    if (!isWrite && itemPermission.resource == "*" && (APP_NAME.indexOf(itemPermission.app) > -1 || itemPermission.app == "")){
      return true;
    }
    // Check for all access permission (write)
    return isWrite &&
      itemPermission.accessType == AccessType.WRITE &&
      itemPermission.resource == "*"  &&
      (APP_NAME.indexOf(itemPermission.app) > -1 || itemPermission.app == "");
  },
  /**
   * Populate all menu in the items property, and check if the user have access to it
   * @param {PermissionItem[]} permissionList List of the permission that owned by user.
   * @param {IMenuNavigation} menu Navigation menu object.
   * @returns {boolean}
   */
  _validateMenuPermission(permissionList: PermissionItem[], menu: IMenuNavigation): boolean {
    const allLinkInMenu = this._getAllMenus(menu);
    return this._validateBaseMenuPermission(permissionList, allLinkInMenu);
  },
  /**
   * Validate single menu permission, return true if the user have permission
   * @param {PermissionItem[]} permissionList List of the permission that owned by user.
   * @param {string} link Navigation menu object.
   * @returns {boolean}
   */
  _validateChildMenuPermission(permissionList: PermissionItem[], link: string): boolean {
    const allLinkInMenu = [link];
    return this._validateBaseMenuPermission(permissionList, allLinkInMenu);
  },
  /**
   * Validate if the menu should visible or not
   * @param {PermissionItem[]} permissionList List of the permission that owned by user.
   * @param {string[]} allLinkInMenu All menu in string array
   * @returns {boolean}
   */
  _validateBaseMenuPermission(permissionList: PermissionItem[], allLinkInMenu: string[]): boolean {
    for (const index in permissionList){
      const singlePermission = permissionList[index];
      if (this._validateAllAccessPermission(singlePermission)){
        return true;
      }
      for (const indexMenu in allLinkInMenu){
        const linkInMenu = allLinkInMenu[indexMenu];
        if (typeof linkInMenu == "undefined"){
          return false;
        }
        if (linkInMenu == singlePermission.resource){
          return true;
        } else {
          const hasEdit = singlePermission.resource.toString().indexOf("*") > -1;
          const urlWithEdit =
            singlePermission.resource.toString().replace("/*", "/");
          const linkInMenuCompare = linkInMenu + "/";
          if (!hasEdit && linkInMenuCompare == urlWithEdit){
            return true;
          }
          if (hasEdit && linkInMenuCompare.toString().indexOf(urlWithEdit) > -1){
            return true;
          }
        }
      }
    }
    return false;
  },
  /**
   * Validate if the user able to see the page or not (via url)
   * @param {PermissionItem[]} permissionList List of the permission that owned by user.
   * @param {Route} route Route object.
   * @returns {boolean}
   */
  _validateRoutePermission(permissionList: PermissionItem[], route: Route): boolean {
    for (const index in permissionList){
      const singlePermission = permissionList[index];
      if (this._validateAllAccessPermission(singlePermission)){
        return true;
      }

      let urlPermissionEdited = singlePermission.resource.toString().replace("/*", "");
      let urlTargetEdited = route.path.toString().replace("/*", "");
      const isEditPage =
        typeof route.path != "undefined" &&
        typeof route.params.id != "undefined";
      let isMatch1 = urlPermissionEdited.indexOf(urlTargetEdited.toString());
      let isMatch2 = urlTargetEdited.indexOf(urlPermissionEdited.toString());

      if (isEditPage && singlePermission.accessType == "W" && (isMatch1 > -1 || isMatch2 > -1)){
        return true;
      }
      urlPermissionEdited += "/";
      urlTargetEdited += "/";
      isMatch1 = urlPermissionEdited.indexOf(urlTargetEdited.toString());
      isMatch2 = urlTargetEdited.indexOf(urlPermissionEdited.toString());
      // If is non-edit page. Whatever user has permission Read or Write system will allow it.
      if (!isEditPage && (isMatch1 > -1 || isMatch2 > -1)){
        return true;
      }
    }
    return false;
  },
  /**
   * Validate if the user able to click an action button
   * @param {PermissionItem[]} permissionList List of the permission that owned by user.
   * @param {IActionButtonPermission} params Information of the button
   * @returns {boolean}
   */
  _validateActionButtonPermission(permissionList: PermissionItem[], params: IActionButtonPermission): boolean {
    const currentPath = window.location.pathname;
    const isSuperAdmin = this.isSuperAdmin();
    // If current user is not system admin and the current part is system admin module, then don't allow user to access.
    if (!isSuperAdmin && SystemAdminModule.indexOf(currentPath) >= 0){
      return false;
    }
    for (const index in permissionList){
      const singlePermission = permissionList[index];

      if (typeof params.item == "undefined"){
        // Default validation to show/hide button if the user has write access
        if (this._validateAllAccessPermission(singlePermission, true)){
          return true;
        }
        if (singlePermission.accessType == AccessType.WRITE){
          const urlEdited = singlePermission.resource.toString().replace("/*", "");
          if (currentPath.toString().indexOf(urlEdited) > -1){
            return true;
          }
        }
      } else {
        // Specific validation for disable/enable button based on the permission with type "Button"
        const actionButtonEnum = params.item;
        if (singlePermission.code == actionButtonEnum){
          return true;
        }
      }
    }
    return false;
  },
  /**
   * Validate if the user able to access specific configuration
   * @param {PermissionItem[]} permissionList List of the permission that owned by user.
   * @param {IConfigurationPermissionItem} params Information of the configuration
   * @returns {boolean}
   */
  _validatePermissionConfiguration(permissionList: PermissionItem[], params: IConfigurationPermissionItem): boolean {
    const permissionListFiltered = permissionList.filter(r => r.app == APP_CONFIG);
    for (const index in permissionListFiltered){
      const singlePermission = permissionListFiltered[index];
      if (this._validateAllAccessPermission(singlePermission) || singlePermission.code == params.type.toString()){
        return true;
      }
      if (typeof params.itemToValidate != "undefined" && params.itemToValidate.toString() === singlePermission.resource){
        return true;
      }
    }
    return false;
  },
  /**
   * Validate if the user able to access widget
   * @param {PermissionItem[]} permissionList List of the permission that owned by user.
   * @param {WidgetEnum} params Widget code
   * @returns {boolean}
   */
  _validatePermissionWidget(permissionList: PermissionItem[], params: WidgetEnum): boolean {
    const permissionListFiltered = permissionList.filter(r => r.code.indexOf("WIDGET_") > -1);
    for (const index in permissionListFiltered){
      const singlePermission = permissionListFiltered[index];
      if (this._validateAllAccessPermission(singlePermission) || singlePermission.code == params.toString()){
        return true;
      }
    }
    return false;
  }
};
interface IPermissionCache {
  version: string,
  items: string[]
}
class PermissionItem {
  code: string;
  resource: string;
  hotelId: string[];
  accessType: string;
  app = "*";
  source_column = "";
  constructor(item: any) {
    this.code = item.code;
    this.resource = item.resource;
    this.hotelId = item.hotel_id.toString().split(",");
    this.accessType = item.access_type;
    this.app = item.app ? item.app : this.app;
    this.source_column = item.source_column;
  }
}
export default PermissionHelper;
